import { useEffect, useState } from "react";

interface WindowSize {
  width: number;
  height: number;
}

const getContainerSize = (): WindowSize => {
  const el = document.querySelector(".map-editor-container");
  return {
    width: el?.clientWidth || window.innerWidth || 0,
    height: el?.clientHeight || window.innerHeight || 0
  };
};

/**
 * Size of the canvas container (`.map-editor-container`), tracked with a
 * ResizeObserver so layout changes that don't fire a window resize —
 * collapsing/expanding the editor sidebars — also update the stage size.
 */
export const useWindowSize = (): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>(getContainerSize);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(getContainerSize());

      setTimeout(() => {
        setWindowSize(getContainerSize());
      }, 20);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial size

    // The container may mount after this hook runs (it lives deeper in the
    // tree), so observe it lazily and re-check on the next frame if missing.
    // Bounded: a host without that container (the SVG editor, viewers)
    // must not pay a DOM query every frame for the life of the page.
    let observer: ResizeObserver | undefined;
    let rafId = 0;
    let attempts = 0;
    const MAX_ATTEMPTS = 120;
    const observeContainer = () => {
      const el = document.querySelector(".map-editor-container");
      if (!el) {
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) {
          rafId = requestAnimationFrame(observeContainer);
        }
        return;
      }
      observer = new ResizeObserver(() => {
        setWindowSize(getContainerSize());
      });
      observer.observe(el);
    };
    observeContainer();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, []);

  return windowSize;
};
